import type { PropsWithChildren, ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { Popover, PopoverPanel } from '@headlessui/react'
import { usePopper } from 'react-popper'
import { Editor, Range } from 'slate'
import { ReactEditor, useSlate } from 'slate-react'
import { twJoin } from 'tailwind-merge'
import BoldIcon from 'shared/icons/bold-icon'
import ItalicIcon from 'shared/icons/italic-icon'
import LinkIcon from 'shared/icons/link-icon'
import ListIcon from 'shared/icons/list-icon'
import NumericListIcon from 'shared/icons/numeric-list-icon'
import SaveIcon from 'shared/icons/save-icon'
import UnlinkIcon from 'shared/icons/unlink-icon'
import { isMarkActive, toggleBlock, toggleMark } from 'modules/community/hooks/slate-editor-utils'
import { isBlockActive } from 'modules/community/utils/editor/core'
import { insertLink, unwrapLink } from 'modules/community/utils/editor/link'

type AnchorElement = {
  clientWidth: number
  clientHeight: number
  getBoundingClientRect: () => DOMRect
}

const computeAnchorEl = (): AnchorElement => {
  const native = globalThis.getSelection()
  const range = native!.getRangeAt(0)
  const rect = range.getBoundingClientRect()
  return {
    clientWidth: rect.width,
    clientHeight: rect.height,
    getBoundingClientRect: () => range.getBoundingClientRect(),
  }
}

function SlateBlockButton({
  format,
  label,
  children,
}: PropsWithChildren<{
  format: string
  label: string
}>): ReactNode {
  const editor = useSlate()
  const active = isBlockActive(editor, format)
  return (
    <button
      className={twJoin(
        'relative z-10 inline-flex items-center px-2 py-1',
        active ? 'fill-black' : 'fill-text-secondary hover:fill-black',
      )}
      onMouseDown={event => {
        event.preventDefault()
        toggleBlock(editor, format)
      }}
      title={label}
      type="button"
    >
      {children}
    </button>
  )
}

function SlateMarkButton({
  format,
  label,
  children,
}: PropsWithChildren<{ format: string; label: string }>): ReactNode {
  const editor = useSlate()
  const active = isMarkActive(editor, format)
  return (
    <button
      className={twJoin(
        'relative z-10 inline-flex items-center px-2 py-1',
        active ? 'fill-black' : 'fill-text-secondary hover:fill-black',
      )}
      onMouseDown={event => {
        event.preventDefault()
        toggleMark(editor, format)
      }}
      title={label}
      type="button"
    >
      {children}
    </button>
  )
}

function SlateLinkButton({
  format,
  label,
  setLinkIsEditing,
}: {
  format: string
  label: string
  setLinkIsEditing: (isEditing: boolean) => void
}): ReactNode {
  const editor = useSlate()
  const active = isBlockActive(editor, format)

  return (
    <button
      className={twJoin(
        'relative z-10 inline-flex items-center px-2 py-1',
        active ? 'fill-black' : 'fill-text-secondary hover:fill-black',
      )}
      onMouseDown={event => {
        event.preventDefault()
        if (active) {
          unwrapLink(editor)
        } else {
          setLinkIsEditing(true)
        }
      }}
      title={label}
      type="button"
    >
      {active ? <UnlinkIcon height="15px" /> : <LinkIcon height="15px" />}
    </button>
  )
}

export default function SlateHoveringToolbar(): ReactNode {
  const [open, setOpen] = useState(false)
  const editor = useSlate()
  const { selection } = editor
  const [referenceElement, setReferenceElement] = useState<AnchorElement>()
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement)
  const [linkIsEditing, setLinkIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (
      !selection ||
      !ReactEditor.isFocused(editor) ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ''
    ) {
      setOpen(false)
      return
    }
    setReferenceElement(computeAnchorEl())
    setOpen(true)
  }, [editor, selection])

  const handleSaveLink = (): void => {
    if (inputRef.current) {
      const url = inputRef.current.value
      if (url) insertLink(editor, url)
      setLinkIsEditing(false)
    }
  }

  const closeOnEnterKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleSaveLink()
    }
  }

  const popperRefCallback = (element: HTMLDivElement | null): void => {
    setPopperElement(element)
  }

  return (
    <Popover>
      <>
        {open && (
          <div>
            <PopoverPanel
              static
              className="absolute left-1/2 z-10 mt-3 rounded-lg bg-background-secondary p-2 shadow-md"
              ref={popperRefCallback}
              style={styles.popper}
              {...attributes.popper}
            >
              {!linkIsEditing && (
                <>
                  <SlateMarkButton format="bold" label="bold">
                    <BoldIcon height="15px" />
                  </SlateMarkButton>
                  <SlateMarkButton format="italic" label="italic">
                    <ItalicIcon height="15px" />
                  </SlateMarkButton>
                  <SlateBlockButton format="bulleted-list" label="bulleted-list">
                    <ListIcon height="15px" />
                  </SlateBlockButton>
                  <SlateBlockButton format="numbered-list" label="numbered-list">
                    <NumericListIcon height="15px" />
                  </SlateBlockButton>
                  <SlateLinkButton setLinkIsEditing={setLinkIsEditing} format="link" label="link" />
                </>
              )}
              {linkIsEditing && (
                <div className="flex">
                  <input
                    className="block w-32 border-2"
                    ref={inputRef}
                    onKeyDown={closeOnEnterKey}
                  />
                  <SaveIcon
                    className="ml-1 cursor-pointer self-center fill-text-secondary"
                    height="20px"
                    onClick={handleSaveLink}
                  />
                </div>
              )}
            </PopoverPanel>
          </div>
        )}
      </>
    </Popover>
  )
}
